import { useState } from 'react'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Input } from '../../components/atoms/Input'
import { Spinner } from '../../components/atoms/Spinner'
import { Th, Td } from '../../components/molecules/Table'
import { TableShell } from '../../components/molecules/TableShell'
import { Field } from '../../components/molecules/Field'
import { useGstRates, type GstRateDto } from '../../../viewmodels/useGstRates'
import { useAuth } from '../../../context/AuthContext'
import { SCREENCODES } from '../../../constants/screen.constants'

/**
 * /manage/gst-rates — catalog with one row marked Current. The
 * Current rate is auto-applied to every new purchase entry across
 * Manufacturing, Warehouse, and Manage purchases.
 *
 * 2026-06-19: rate split into CGST + SGST. Each row carries the two
 * components; total GST is rendered as the sum.
 *
 * No edit — rates are immutable; add a new row for a new rate.
 */
export function GstRatesPage(): JSX.Element {
  const vm = useGstRates()
  const { can } = useAuth()
  const canAdd = can(SCREENCODES.MANAGEGSTRATES, 'add')
  const canEdit = can(SCREENCODES.MANAGEGSTRATES, 'edit')
  const canDelete = can(SCREENCODES.MANAGEGSTRATES, 'delete')

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-heading text-h2 text-charcoal">GST Rates</h1>
        <p className="font-body text-body-sm text-text-secondary mt-1">
          The rate marked <span className="font-medium">Current</span> is auto-applied to every new
          purchase entry. Each rate is a CGST + SGST pair; the total is the sum. Rates are
          immutable once added — for a new rate, add a new row.
        </p>
      </header>

      {vm.error !== null ? (
        <Alert tone="error">
          {vm.error}{' '}
          <button
            type="button"
            className="underline underline-offset-2 hover:text-charcoal"
            onClick={() => vm.reload()}
          >
            Retry
          </button>
        </Alert>
      ) : null}

      {canAdd ? <AddForm onAdd={vm.add} /> : null}

      {vm.isLoading ? (
        <div className="flex items-center justify-center py-12"><Spinner /></div>
      ) : vm.items.length === 0 ? (
        <EmptyState />
      ) : (
        <Table
          items={vm.items}
          canEdit={canEdit}
          canDelete={canDelete}
          onSetCurrent={vm.setCurrent}
          onDelete={vm.remove}
        />
      )}
    </div>
  )
}

function EmptyState(): JSX.Element {
  return (
    <div className="text-center py-16 border border-border rounded-md bg-parchment/40">
      <p className="font-heading text-h3 text-charcoal">No GST rates yet</p>
      <p className="font-body text-body-sm text-text-secondary mt-2">
        Add your first rate above. The first rate becomes Current automatically.
      </p>
    </div>
  )
}

function AddForm({
  onAdd
}: {
  readonly onAdd: (input: { cgst_bps: number; sgst_bps: number; description: string | null; set_as_current: boolean }) => Promise<{ ok: true } | { ok: false; field: string | null; message: string }>
}): JSX.Element {
  const [cgstText, setCgstText] = useState<string>('')
  const [sgstText, setSgstText] = useState<string>('')
  const [description, setDescription] = useState<string>('')
  const [setCurrent, setSetCurrent] = useState<boolean>(true)
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [fieldError, setFieldError] = useState<{ field: 'cgstText' | 'sgstText' | 'description'; message: string } | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  const parseComponent = (text: string): { ok: true; bps: number } | { ok: false; message: string } => {
    const trimmed = text.trim()
    if (trimmed === '') return { ok: false, message: 'Required' }
    const n = Number(trimmed)
    if (!Number.isFinite(n) || n < 0 || n > 25) {
      return { ok: false, message: 'Must be between 0 and 25 percent' }
    }
    return { ok: true, bps: Math.round(n * 100) }
  }

  const handleSubmit = async (): Promise<void> => {
    setFieldError(null)
    setFormError(null)

    const c = parseComponent(cgstText)
    if (!c.ok) {
      setFieldError({ field: 'cgstText', message: c.message })
      return
    }
    const s = parseComponent(sgstText)
    if (!s.ok) {
      setFieldError({ field: 'sgstText', message: s.message })
      return
    }

    setSubmitting(true)
    const r = await onAdd({
      cgst_bps: c.bps,
      sgst_bps: s.bps,
      description: description.trim() === '' ? null : description.trim(),
      set_as_current: setCurrent
    })
    setSubmitting(false)
    if (!r.ok) {
      const f = r.field
      if (f === 'cgst_bps') setFieldError({ field: 'cgstText', message: r.message })
      else if (f === 'sgst_bps') setFieldError({ field: 'sgstText', message: r.message })
      else if (f === 'description') setFieldError({ field: 'description', message: r.message })
      else setFormError(r.message)
      return
    }
    setCgstText('')
    setSgstText('')
    setDescription('')
    setSetCurrent(true)
  }

  const totalText = (() => {
    const ctrim = cgstText.trim()
    const strim = sgstText.trim()
    if (ctrim === '' && strim === '') return null
    const c = Number(ctrim)
    const s = Number(strim)
    if (!Number.isFinite(c) || !Number.isFinite(s)) return null
    return `${(c + s).toFixed(2)}%`
  })()

  return (
    <div className="border border-border rounded-md bg-parchment/40 p-4 space-y-3">
      <p className="font-body text-body-sm font-medium text-charcoal">Add a new rate</p>
      {formError !== null ? <Alert tone="error">{formError}</Alert> : null}
      <div className="grid grid-cols-1 sm:grid-cols-[6rem_6rem_1fr_auto] gap-3 items-end">
        <Field label="CGST (%)" error={fieldError?.field === 'cgstText' ? fieldError.message : null}>
          <Input
            value={cgstText}
            hasError={fieldError?.field === 'cgstText'}
            onChange={(e) => setCgstText(e.target.value)}
            placeholder="e.g. 9"
            inputMode="decimal"
          />
        </Field>
        <Field label="SGST (%)" error={fieldError?.field === 'sgstText' ? fieldError.message : null}>
          <Input
            value={sgstText}
            hasError={fieldError?.field === 'sgstText'}
            onChange={(e) => setSgstText(e.target.value)}
            placeholder="e.g. 9"
            inputMode="decimal"
          />
        </Field>
        <Field label="Description (optional)" error={fieldError?.field === 'description' ? fieldError.message : null}>
          <Input
            value={description}
            hasError={fieldError?.field === 'description'}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. Standard 18%"
          />
        </Field>
        <Button onClick={() => void handleSubmit()} disabled={submitting}>
          {submitting ? 'Adding…' : 'Add'}
        </Button>
      </div>
      {totalText !== null ? (
        <p className="font-body text-body-xs text-text-secondary">
          Total GST: <span className="font-mono text-charcoal">{totalText}</span>
        </p>
      ) : null}
      <label className="inline-flex items-center gap-2 font-body text-body-sm">
        <input
          type="checkbox"
          checked={setCurrent}
          onChange={(e) => setSetCurrent(e.target.checked)}
        />
        Set as current (unflips any existing current rate)
      </label>
    </div>
  )
}

function formatBps(bps: number): string {
  return `${(bps / 100).toFixed(2)}%`
}

function Table({
  items,
  canEdit,
  canDelete,
  onSetCurrent,
  onDelete
}: {
  readonly items: GstRateDto[]
  readonly canEdit: boolean
  readonly canDelete: boolean
  readonly onSetCurrent: (id: string) => Promise<{ ok: true } | { ok: false; message: string }>
  readonly onDelete: (id: string) => Promise<{ ok: true } | { ok: false; message: string }>
}): JSX.Element {
  const [busyId, setBusyId] = useState<string | null>(null)
  const [rowError, setRowError] = useState<string | null>(null)

  const handleSetCurrent = async (id: string): Promise<void> => {
    setBusyId(id)
    setRowError(null)
    const r = await onSetCurrent(id)
    setBusyId(null)
    if (!r.ok) setRowError(r.message)
  }

  const handleDelete = async (id: string): Promise<void> => {
    if (typeof window !== 'undefined' && !window.confirm('Delete this rate?')) return
    setBusyId(id)
    setRowError(null)
    const r = await onDelete(id)
    setBusyId(null)
    if (!r.ok) setRowError(r.message)
  }

  return (
    <div className="space-y-3">
      {rowError !== null ? <Alert tone="error">{rowError}</Alert> : null}
      <TableShell>
        <table className="w-full font-body text-body-sm">
          <thead className="bg-parchment/50 text-text-secondary">
            <tr>
              <Th className="text-right">CGST</Th>
              <Th className="text-right">SGST</Th>
              <Th className="text-right">Total GST</Th>
              <Th>Description</Th>
              <Th>Status</Th>
              <Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr key={row.id} className="border-t border-border">
                <Td className="text-right font-mono">{formatBps(row.cgst_bps)}</Td>
                <Td className="text-right font-mono">{formatBps(row.sgst_bps)}</Td>
                <Td className="text-right font-mono font-medium">{formatBps(row.cgst_bps + row.sgst_bps)}</Td>
                <Td>{row.description ?? <span className="text-text-secondary">—</span>}</Td>
                <Td>
                  {row.is_current ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-forest/15 text-forest font-medium text-body-xs">
                      ● Current
                    </span>
                  ) : (
                    <span className="text-text-secondary">—</span>
                  )}
                </Td>
                <Td className="text-right whitespace-nowrap">
                  {!row.is_current && canEdit ? (
                    <>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => void handleSetCurrent(row.id)}
                        disabled={busyId === row.id}
                      >
                        {busyId === row.id ? '…' : 'Set current'}
                      </Button>{' '}
                    </>
                  ) : null}
                  {!row.is_current && canDelete ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => void handleDelete(row.id)}
                      disabled={busyId === row.id}
                    >
                      Delete
                    </Button>
                  ) : null}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableShell>
    </div>
  )
}
